import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enUS from "./locales/en-US.json";
import zhCN from "./locales/zh-CN.json";

export const LANGUAGES = [
  { code: "zh-CN", label: "简体中文", short: "中" },
  { code: "en-US", label: "English", short: "EN" },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]["code"];

export const DEFAULT_LANGUAGE: LanguageCode = "zh-CN";
export const LANGUAGE_STORAGE_KEY = "pl.locale";

/** "en", "en-GB", "zh" -> the supported language we actually ship. */
export function normalizeLanguage(lng: string | undefined | null): LanguageCode {
  if (!lng) return DEFAULT_LANGUAGE;
  const lower = lng.toLowerCase();
  if (lower.startsWith("zh")) return "zh-CN";
  if (lower.startsWith("en")) return "en-US";
  return DEFAULT_LANGUAGE;
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      "zh-CN": { translation: zhCN },
      "en-US": { translation: enUS },
    },
    // 注意：i18next v25 的 supportedLngs + nonExplicitSupportedLngs 组合存在
    // 查找回归（t() 返回键名），这里改用显式 fallback 映射覆盖区域变体：
    // en-GB/en → en-US，zh/zh-Hans/zh-Hant → zh-CN，其余 → zh-CN。
    fallbackLng: {
      zh: ["zh-CN"],
      "zh-Hans": ["zh-CN"],
      "zh-Hant": ["zh-CN"],
      "zh-CN": ["zh-CN"],
      en: ["en-US"],
      "en-GB": ["en-US"],
      "en-US": ["en-US"],
      default: [DEFAULT_LANGUAGE],
    },
    supportedLngs: LANGUAGES.map((item) => item.code),
    defaultNS: "translation",
    interpolation: { escapeValue: false },
    returnNull: false,
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },
  });

/** Keep <html lang> in sync so fonts, quotes and screen readers follow the UI language. */
export function applyDocumentLanguage(lng: string): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = normalizeLanguage(lng);
  }
}

i18n.on("languageChanged", (lng) => {
  applyDocumentLanguage(lng);
});

applyDocumentLanguage(i18n.language);

export default i18n;
